import { IElement, IGuide } from 'common/interfaces';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import ElemsService from 'services/ElemsService';
import { setCanvasRef } from 'store/reducers/common.slice';
import { groupElemsWhenChildElemTransformStop } from 'store/reducers/elems.slice';
import { setStickyPoints } from 'store/reducers/guides.slice';

import useBreakpoints from './useBreakpoints';

interface IUseCanvas {
  contentLoaded: boolean;
  canvasRef: React.RefObject<HTMLDivElement>;
  elems: IElement[];
  guides: IGuide[];
  isGuidesVisible: boolean;
  addNewBreakpoint: (artboardWidth: number) => void;
}

export default function useCanvas(): IUseCanvas {
  const canvasRef = useRef<HTMLDivElement>(null);
  const elems = useTypedSelector((state) => state.elems.elems);
  const guides = useTypedSelector((state) => state.guides.guides);
  const isGuidesVisible = useTypedSelector(
    (state) => state.guides.isGuidesVisible,
  );

  const isGroupResizeEvent = useTypedSelector(
    (state) => state.common.isMultipleStateUpdateEvent,
  );
  const needToGroupAgainCollection = useTypedSelector(
    (state) => state.elems.needToGroupAgainCollection,
  );
  const selectedElemsIds = useTypedSelector(
    (state) => state.elems.selectedElemsIds,
  );

  const [contentLoaded, setContentLoaded] = useState(false);

  const dispatch = useDispatch();

  const { addNewBreakpoint } = useBreakpoints(elems, guides, dispatch);

  const handleLoadContent = useCallback((): void => {
    setContentLoaded(true);
  }, []);

  window.addEventListener('load', handleLoadContent);

  // delete, load event listener
  useEffect(() => {
    return () => window.removeEventListener('load', handleLoadContent);
  }, [handleLoadContent]);

  // When the page loads, scroll the canvas to the artboard (to the center of the x- and y-axis)
  useEffect(() => {
    if (contentLoaded) {
      const startScrollOffsetX =
        document.documentElement.scrollWidth / 2 -
        document.documentElement.clientWidth / 2;

      const startScrollOffsetY =
        document.documentElement.scrollHeight / 2 -
        document.documentElement.clientHeight / 2;

      window.scrollTo(startScrollOffsetX, startScrollOffsetY);
    }
  }, [contentLoaded]);

  // save the link to the canvas in redux
  useEffect(() => {
    if (canvasRef.current) {
      dispatch(setCanvasRef(canvasRef));
    }
  }, [dispatch, canvasRef]);

  // update sticky points when changing guides or elements
  useEffect(() => {
    // TODO: могут возникнуть баги
    if (isGroupResizeEvent) return;

    const guidesData = isGuidesVisible ? guides : [];
    const stickyPoints = ElemsService.getElemsStickyPoints(elems, guidesData);

    dispatch(setStickyPoints(stickyPoints));
  }, [elems, guides, dispatch, isGuidesVisible, isGroupResizeEvent]);

  // group the child elements again after completing the change of the group child element
  useEffect(() => {
    if (!needToGroupAgainCollection.length) return;

    if (!selectedElemsIds.length) {
      dispatch(groupElemsWhenChildElemTransformStop());
      return;
    }

    const isNeedToGroupElems = needToGroupAgainCollection.find((item) => {
      return item.childrenIds.find((id) => selectedElemsIds.includes(id))
        ? false
        : true;
    });

    if (isNeedToGroupElems) {
      dispatch(groupElemsWhenChildElemTransformStop());
    }
  }, [selectedElemsIds, needToGroupAgainCollection, dispatch]);

  // скролим экран в позицию курсора при изменении масштаба
  useEffect(() => {
    const handleWindowResize = (e: any): void => {
      if (e.ctrlKey) {
        // console.log('WHEEL------', e);
        const zoom = ((window.outerWidth - 10) / window.innerWidth) * 100;

        // console.log('zoom------', zoom);
        const xOffset =
          e.pageX -
          (window.pageXOffset + document.documentElement.clientWidth / 2);
        const yOffset =
          e.pageY -
          (window.pageYOffset + document.documentElement.clientHeight / 2);

        // console.log('xOffset----yOffset', xOffset, yOffset);
        // window.scrollBy(xOffset / 10, yOffset / 10);

        // const startScrollOffsetX =
        //   document.documentElement.scrollWidth / 2 -
        //   document.documentElement.clientWidth / 2;

        // const startScrollOffsetY =
        //   document.documentElement.scrollHeight / 2 -
        //   document.documentElement.clientHeight / 2;

        // window.scrollBy(5, 5);
        // window.scrollBy(xOffset / 5, yOffset / 5);
        // // console.log('WHEEL------', window);
        // const xOffset =
        //   e.pageX -
        //   (window.pageXOffset + document.documentElement.clientWidth / 2);
        // const yOffset =
        //   e.pageY -
        //   (window.pageYOffset + document.documentElement.clientHeight / 2);

        // // console.log('xOffset----yOffset', xOffset, yOffset);
        // // window.scrollBy(xOffset / 10, yOffset / 10);

        // const startScrollOffsetX =
        //   document.documentElement.scrollWidth / 2 -
        //   document.documentElement.clientWidth / 2;

        // const startScrollOffsetY =
        //   document.documentElement.scrollHeight / 2 -
        //   document.documentElement.clientHeight / 2;

        // window.scrollTo(
        //   window.pageXOffset + xOffset / 3,
        //   window.pageYOffset + yOffset / 3,
        // );
        // // window.scrollBy(xOffset / 5, yOffset / 5);
      }
    };

    document.addEventListener('mousewheel', handleWindowResize);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', function (e: any) {
      // console.log('Resize event----------', e);
      const zoom = ((window.outerWidth - 10) / window.innerWidth) * 100;

      // console.log(
      //   'Resize event ZOOM----------',
      //   Math.round(window.devicePixelRatio * 100),
      // );
      // console.log('window.devicePixelRatio----------', window.devicePixelRatio);
    });
  }, []);

  useEffect(() => {
    console.log('elems changed------------', elems);
  }, [elems]);

  return {
    contentLoaded,
    canvasRef,
    isGuidesVisible,
    elems,
    guides,
    addNewBreakpoint,
  };
}
